
import { computed, defineComponent, PropType, reactive, ref, watch } from 'vue'
import { CtCatalogue, CtClient } from '@/models/interfaces/content-type'
import { useStore } from 'vuex'
import ChannelCard from '@/pages/Channels/components/ChannelCard.vue'
import { useRouter } from 'vue-router'
import ListHeader from '@/components/ListHeader.vue'
import { Entry, generateUUID, UUID } from '@Protium/vue-ozone-utils'
import CloseIcon from '@/components/Icons/CloseIcon.vue'
import useDiffSaveHandler, { SaveHooks } from '@/pages/Manage/Catalogues/composables/useDiffSaveHandler'
import { ContentType } from '@/services/directr'
import useContentModule, { Mutation } from '@/pages/composables/useContentModule'
import AddIcon from '@/components/Icons/AddIcon.vue'
import { useQuasar } from 'quasar'
import TickIcon from '@/components/Icons/TickIcon.vue'
import { cloneDeep } from 'lodash'

enum FormInputType {
  String = 'string',
  Number = 'number',
  Object = 'object',
  Boolean = 'boolean',
  Toggle = 'toggle',
  Array = 'array',
  'Multi-select' = 'multi-select',
  Select = 'select',
  Tag = 'tag',
  'Link-list' = 'link-list',
  Link = 'link',
  Placeholder = 'placeholder',
  Nullable = 'NULL',
}

function getTypeDefault (type: string) {
  switch (type) {
    case FormInputType.String:
      return ''
    case FormInputType.Number:
      return 0
    case FormInputType.Array:
    case FormInputType.Select:
    case FormInputType.Tag:
      return []
    case FormInputType.Object:
      return {}
    case FormInputType['Link-list']:
    case FormInputType.Link:
    case FormInputType.Placeholder:
    case FormInputType.Nullable:
      return null
    case FormInputType.Boolean:
      return false
    default:
      return null
  }
}

export default defineComponent({
  name: 'ClientChannels',
  components: { TickIcon, AddIcon, CloseIcon, ListHeader, ChannelCard },
  props: {
    client: {
      type: Object as PropType<CtClient>,
      required: true,
    },
  },
  setup (props) {
    const $store = useStore()
    const $router = useRouter()
    const $directr = $store.getters['Services/Directr']
    const $q = useQuasar()

    const editing = ref(false)

    const {
      model,
      fetch,
      requestHandler,
    } = useContentModule<CtCatalogue>('Catalogues', $store)
    const saveHooks: SaveHooks = {}
    const {
      diffState,
      currentDiff,
      save: saveDiffState
    } = useDiffSaveHandler<CtCatalogue>(ContentType.Catalogue, $directr, saveHooks, [])
    void fetch([]).then(items => {
      diffState.lhs = cloneDeep(items) ?? []
      diffState.rhs = cloneDeep(items) ?? []
    })

    const channels = computed(() => diffState.rhs?.filter(c => c.client === props.client.sys.id))

    watch(
      () => props.client,
      () => void fetch([])
    )
    const onChannelClick = (channelSysId) => {
      void $router.push({ name: 'Channels', params: { channelId: channelSysId } })
    }

    const channelDialog = reactive({
      show: false,
      collection: computed(
        () => diffState.lhs
          .filter(c => c.client !== props.client.sys.id)
      ),
      selection: [] as UUID[],
      select: (item: Entry) => {
        const index = channelDialog.selection.findIndex(s => s === item?.sys?.id)
        if (index >= 0) {
          channelDialog.selection.splice(index, 1)
        } else {
          channelDialog.selection.push(item.sys?.id)
        }
      },
      model,
      requestHandler,
      addChannel: () => {
        $q.dialog({
          title: 'Create a new Channel',
          message: 'Please enter a channel name',
          prompt: {
            model: '',
            type: 'text',
            isValid: val => Boolean(val.length),
          },
          cancel: true,
        }).onOk((name) => {
          editing.value = true
          const ids: Array<number> = diffState.lhs
            .concat(diffState.rhs)
            .map(cat => parseInt(cat.id as any))
            .sort((a, b) => a - b)
          const id = ids[ids.length - 1] + 1
          const modelValue = model.value
          if (modelValue) {
            const data = Object.fromEntries(Object.keys(modelValue).map(k => {
              if (modelValue[k].default === null) {
                return ([k, null])
              }
              return ([k, modelValue[k].default || getTypeDefault(modelValue[k].type)])
            }))
            data.name = name
            data.id = id
            data.client = props.client.sys.id
            data.sys = {
              id: generateUUID()
            }
            diffState.rhs.push(data as CtCatalogue)
            channelDialog.show = false
            channelDialog.selection = []
          }
        }).onCancel(() => {
          // console.log('>>>> Cancel')
        }).onDismiss(() => {
          // console.log('I am triggered on both OK and Cancel')
        })
      }
    })

    const addDialogSelection = () => {
      if (channelDialog.selection.length > 0) {
        const channelsWithClients = channelDialog.collection
          .filter(c => channelDialog.selection.includes(c.sys.id) && c.client?.length)
        if (channelsWithClients.length) {
          $q.dialog({
            title: 'Add channels to client?',
            message: '<span style="color: red">Channels will be unlinked from existing clients.</span>',
            html: true,
            cancel: true,
          }).onOk(() => {
            editing.value = true
            diffState.rhs = diffState.rhs.map(c => {
              if (channelDialog.selection.includes(c.sys.id)) {
                return {
                  ...c,
                  client: props.client.sys.id
                }
              }
              return c
            })
            channelDialog.show = false
            channelDialog.selection = []
          })
        } else {
          editing.value = true
          diffState.rhs = diffState.rhs.map(c => {
            if (channelDialog.selection.includes(c.sys.id)) {
              return {
                ...c,
                client: props.client.sys.id
              }
            }
            return c
          })
          channelDialog.show = false
          channelDialog.selection = []
        }
      }
    }

    const toUnlink = ref<UUID[]>([])

    const unlinkChannel = (channelSysId) => {
      const channelIndex = diffState.rhs.findIndex(c => c.sys.id === channelSysId)
      $q.dialog({
        title: `Unlink client from channel ${ diffState.rhs[channelIndex]?.name }?`,
        message: 'Are you sure?',
        cancel: true,
        persistent: false
      }).onOk(() => {
        editing.value = true
        toUnlink.value.push(channelSysId)
      })
    }

    const cancel = () => {
      diffState.rhs = cloneDeep(diffState.lhs)
      toUnlink.value = []
      editing.value = false
    }

    const save = () => {
      diffState.rhs = diffState.rhs.map(c => {
        if (toUnlink.value.includes(c.sys.id)) {
          c.client = null
        }
        return c
      })
      void saveDiffState().then(() => {
        diffState.lhs = cloneDeep(diffState.rhs)
        toUnlink.value = []
        editing.value = false
      })
    }

    return {
      editing,
      channels,
      diffState,
      channelDialog,
      onChannelClick,
      unlinkChannel,
      toUnlink,
      cancel,
      save,
      addDialogSelection,
    }
  }
})
